import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAuthModal, useGuardedNavigate } from "../../context/AuthModalContext";
import { useNotifications } from "../../context/NotificationContext";
import { useTranslation } from "../../context/TranslationContext";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import IconButton, { STATE_LAYER } from "../ds/IconButton";
import Menu, { MenuItem, MenuSeparator } from "../ds/Menu";
import ShellAvatar from "./ShellAvatar";
import { useDismiss } from "./useShellData";
import { notificationLink, notificationText } from "../../utils/notificationText";
import { formatShortTime } from "../../utils/formatShortTime";
import { getProfileAvatarUrl } from "../../utils/userDisplay";

/*
  Handoff "Top actions" (desktop) and "Top bar · ტელეფონი" + "Tab bar · ტელეფონი"
  (below 1024px), with the Notifications popover / sheet and the profile menu.
*/

const money = (n) => `${(Number(n) || 0).toFixed(2)} ₾`;

/* Handoff "Search field": searches Discover. */
const SearchBox = ({ onClose, className = "" }) => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const [query, setQuery] = useState("");
  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        const q = query.trim();
        navigate(q ? `/?q=${encodeURIComponent(q)}` : "/");
        onClose();
      }}
      className={`flex h-[44px] min-w-0 items-center gap-space-8 rounded-radius-md bg-fill-tertiary pl-[14px] pr-space-8 text-label-tertiary shadow-edge ${className}`}
    >
      <Icon name="search" size={16} />
      <input
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => event.key === "Escape" && onClose()}
        placeholder={t("shell.searchMintly")}
        aria-label={t("common.search")}
        className="min-w-0 flex-1 bg-transparent text-callout text-label-primary outline-none placeholder:text-label-tertiary"
      />
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.cancel")}
        className="grid size-6 place-items-center rounded-full text-label-tertiary hover:text-label-primary"
      >
        <Icon name="close" size={16} />
      </button>
    </form>
  );
};

const iconFor = (type = "") => {
  if (/submission|approved|kyb|kyc/.test(type)) return "check-circle";
  if (/sold|refund|withdraw|payout|earning|balance|purchase|trial|subscription/.test(type)) return "wallet";
  if (/campaign/.test(type)) return "megaphone";
  if (/member|joined|referral|follow|affiliate/.test(type)) return "user-plus";
  if (/message|comment|post|mention|support/.test(type)) return "message";
  return "bell";
};

const DAY = 86400000;
const groupOf = (iso) => {
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const at = new Date(iso).getTime();
  if (at >= startOfToday) return "today";
  if (at >= startOfToday - DAY) return "yesterday";
  return "older";
};

/* Handoff "Notifications popover" + "Notification item" (Unread | Read). */
const NotificationsPanel = ({ onDone, sheet = false }) => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const { notifications, markRead, markAllRead, fetchNotifications } = useNotifications();
  const groups = ["today", "yesterday", "older"]
    .map((key) => [key, notifications.filter((n) => groupOf(n.createdAt) === key)])
    .filter(([, items]) => items.length > 0);

  return (
    <div
      role="dialog"
      aria-label={t("navigation.notifications")}
      className={`flex flex-col overflow-y-auto border border-separator-default bg-bg-overlay px-space-8 pb-space-8 pt-space-4 shadow-overlay ${
        sheet
          ? "max-h-[85vh] w-full rounded-t-radius-xl border-b-0 pb-space-24 animate-ds-sheet-in"
          : "absolute right-[-120px] top-[calc(100%+12px)] z-40 max-h-[min(716px,80vh)] w-[400px] rounded-radius-lg animate-ds-pop-in"
      }`}
    >
      <div className="flex items-center justify-between px-space-12 pb-space-8 pt-space-12">
        <h2 className="text-headline">{t("navigation.notifications")}</h2>
        <div className="flex gap-space-4">
          <IconButton variant="plain" label={t("messages.markAllAsRead")} onClick={markAllRead}>
            <Icon name="check-all" size={18} />
          </IconButton>
          <IconButton variant="plain" label={t("shell.refresh")} onClick={fetchNotifications}>
            <Icon name="refresh" size={18} />
          </IconButton>
        </div>
      </div>
      {groups.length === 0 && <p className="px-space-12 py-space-16 text-footnote text-label-tertiary">{t("messages.noNotifications")}</p>}
      {groups.map(([key, items]) => (
        <section key={key} aria-label={t(`shell.${key}`)}>
          <h3 className="px-space-12 pb-space-4 pt-space-12 text-caption-1-semibold text-label-tertiary">{t(`shell.${key}`)}</h3>
          {items.map((notification) => {
            const unread = !notification.read;
            const link = notificationLink(notification);
            return (
              <button
                key={notification._id}
                type="button"
                onClick={async () => {
                  await markRead(notification._id);
                  if (link) navigate(link);
                  onDone();
                }}
                className={`relative flex w-full gap-space-12 rounded-control p-space-12 text-left ${STATE_LAYER}`}
              >
                <span
                  className={`grid size-10 shrink-0 place-items-center rounded-radius-sm ${
                    unread ? "bg-accent-tinted text-accent-tinted-label" : "bg-fill-tertiary text-label-secondary"
                  }`}
                >
                  <Icon name={iconFor(notification.type)} size={20} />
                </span>
                <span className="flex min-w-0 flex-col gap-space-4">
                  <span className={unread ? "text-subheadline-semibold" : "text-subheadline"}>
                    {notificationText(notification, t)}
                  </span>
                  <span className="text-caption-1 text-label-tertiary">{formatShortTime(notification.createdAt)}</span>
                  {link && (
                    <span className={`text-footnote-semibold ${unread ? "text-accent-text" : "text-label-secondary"}`}>
                      {t("shell.viewDetails")}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
};

/* A popover on desktop; a bottom sheet over a scrim on phones. */
const Popover = ({ open, onClose, anchorRef, sheet, isDark, children }) => {
  const sheetRef = useRef(null);
  const refs = useMemo(() => [anchorRef, sheetRef], [anchorRef]);
  useDismiss(refs, open, onClose);
  if (!open) return null;
  if (!sheet) return children;
  return createPortal(
    <div data-theme={isDark ? "dark" : "light"} className="fixed inset-0 z-[400] font-ds text-label-primary">
      <div className="absolute inset-0 bg-overlay-scrim animate-ds-fade-in" onClick={onClose} aria-hidden="true" />
      <div ref={sheetRef} className="absolute inset-x-0 bottom-0">
        {children}
      </div>
    </div>,
    document.body
  );
};

const BellButton = ({ sheet = false, isDark }) => {
  const { t } = useTranslation();
  const { unreadCount } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  return (
    <div className="relative" ref={ref}>
      <IconButton
        size="m"
        label={t("navigation.notifications")}
        count={unreadCount || undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" size={22} />
      </IconButton>
      <Popover open={open} onClose={close} anchorRef={ref} sheet={sheet} isDark={isDark}>
        <NotificationsPanel onDone={close} sheet={sheet} />
      </Popover>
    </div>
  );
};

/* Figma E6: the balance on its own, the account pages, then signing out. */
const ProfileMenu = ({ balance, anchorRef, open, onClose }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useGuardedNavigate();
  return (
    <Menu open={open} onClose={onClose} anchorRef={anchorRef} width={260} label={t("navigation.viewProfile")}>
      <MenuItem
        icon="wallet"
        onClick={() => navigate("/balance")}
        detail={
          <span className="flex shrink-0 flex-col items-end">
            <span className="text-footnote-semibold text-accent-text">{money(balance.amount + balance.holdBalance)}</span>
            {balance.holdBalance > 0 && (
              <span className="text-caption-1 text-label-tertiary">{t("shell.onHold", { amount: balance.holdBalance.toFixed(2) })}</span>
            )}
          </span>
        }
      >
        {t("shell.balance")}
      </MenuItem>
      <MenuSeparator />
      <MenuItem icon="users" onClick={() => navigate("/profile")}>
        {t("navigation.viewProfile")}
      </MenuItem>
      <MenuItem icon="apps" onClick={() => navigate("/purchases")}>
        {t("navigation.purchases")}
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        icon="logout"
        danger
        onClick={() => {
          logout();
          navigate("/auth");
        }}
      >
        {t("auth.logOut")}
      </MenuItem>
    </Menu>
  );
};

const ProfileButton = ({ balance, compact = false }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const name = user?.username || user?.businessName || "";
  const total = balance.amount + balance.holdBalance;
  return (
    <div className="relative" ref={ref}>
      {compact ? (
        <button type="button" aria-label={name} aria-expanded={open} onClick={() => setOpen((o) => !o)} className="rounded-full">
          <ShellAvatar src={getProfileAvatarUrl(user)} name={name} />
        </button>
      ) : (
        <button
          type="button"
          aria-label={`${name}, ${money(total)}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={`relative flex items-center gap-[10px] rounded-full bg-fill-secondary py-[6px] pl-[6px] pr-[14px] text-left ${STATE_LAYER}`}
        >
          <ShellAvatar src={getProfileAvatarUrl(user)} name={name} />
          <span className="flex flex-col">
            <span className="max-w-[140px] truncate text-footnote-semibold">{name}</span>
            <span className="text-caption-1-semibold text-accent-text">{balance.loaded ? money(total) : "…"}</span>
          </span>
        </button>
      )}
      <ProfileMenu balance={balance} anchorRef={ref} open={open} onClose={close} />
    </div>
  );
};

/* Desktop: the floating pill at the top end of the page. Search grows to the start side. */
export const TopActions = ({ balance, isDark }) => {
  const { t } = useTranslation();
  // While the session is being checked, show neither the account nor Sign in.
  const { isAuthenticated, loading } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [searching, setSearching] = useState(false);
  return (
    <div className="absolute right-space-24 top-space-16 z-30 flex justify-end font-ds text-label-primary">
      <div className="flex items-center gap-space-8 rounded-full border border-separator-default bg-material-regular px-space-8 py-[6px] shadow-overlay backdrop-blur-[30px]">
        {searching ? (
          <SearchBox onClose={() => setSearching(false)} className="w-[280px]" />
        ) : (
          <IconButton size="m" label={t("common.search")} onClick={() => setSearching(true)}>
            <Icon name="search" size={22} />
          </IconButton>
        )}
        <IconButton size="m" label={t("shell.aiSoon")} aria-disabled="true">
          <Icon name="sparkle" size={22} />
        </IconButton>
        {loading ? null : isAuthenticated ? (
          <>
            <BellButton isDark={isDark} />
            <ProfileButton balance={balance} />
          </>
        ) : (
          <Button variant="gray" size="m" onClick={() => openAuthModal({ form: "signin" })}>
            {t("auth.signIn")}
          </Button>
        )}
      </div>
    </div>
  );
};

/* Phone / tablet: top bar. Open search fills the bar, with Cancel. */
export const MobileTopBar = ({ balance, isDark }) => {
  const { t } = useTranslation();
  // While the session is being checked, show neither the account nor Sign in.
  const { isAuthenticated, loading } = useAuth();
  const { openAuthModal } = useAuthModal();
  const navigate = useGuardedNavigate();
  const [searching, setSearching] = useState(false);
  return (
    <header className="fixed inset-x-0 top-0 z-30 flex h-[60px] items-center gap-space-4 border-b border-separator-default bg-material-regular pl-space-16 pr-space-12 font-ds text-label-primary backdrop-blur-[30px]">
      {searching ? (
        <>
          <SearchBox onClose={() => setSearching(false)} className="flex-1" />
          <button
            type="button"
            onClick={() => setSearching(false)}
            className="h-[44px] rounded-radius-md px-space-12 text-caps-button-m uppercase text-accent-text"
          >
            {t("common.cancel")}
          </button>
        </>
      ) : (
        <>
          <button type="button" onClick={() => navigate("/")} className="flex items-center gap-space-8">
            <span className="size-7 shrink-0 rounded-radius-sm bg-accent-default" aria-hidden="true" />
            <span className="text-headline">Mintly</span>
          </button>
          <span className="flex-1" />
          <IconButton size="m" label={t("common.search")} onClick={() => setSearching(true)}>
            <Icon name="search" size={22} />
          </IconButton>
          <IconButton size="m" label={t("shell.aiSoon")} aria-disabled="true">
            <Icon name="sparkle" size={22} />
          </IconButton>
          {loading ? null : isAuthenticated ? (
            <>
              <BellButton sheet isDark={isDark} />
              <ProfileButton balance={balance} compact />
            </>
          ) : (
            <Button variant="gray" size="s" onClick={() => openAuthModal({ form: "signin" })}>
              {t("auth.signIn")}
            </Button>
          )}
        </>
      )}
    </header>
  );
};

/* The tab bar's +: a sheet to create a business or a post. */
const CreateButton = () => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  return (
    <div ref={ref}>
      <button
        type="button"
        aria-label={t("shell.create")}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
        className={`relative grid h-12 w-16 place-items-center rounded-radius-lg bg-accent-default text-accent-on ${STATE_LAYER}`}
      >
        <Icon name="plus" size={24} />
      </button>
      <Menu open={open} onClose={close} anchorRef={ref} align="start" label={t("shell.create")}>
        <MenuItem icon="bag" onClick={() => navigate("/create-business")}>
          {t("navigation.createBusiness")}
        </MenuItem>
        <MenuItem icon="edit" onClick={() => navigate("/home?compose=1", { state: { composing: true } })}>
          {t("navigation.createPost")}
        </MenuItem>
      </Menu>
    </div>
  );
};

/* Phone / tablet: tab bar. Menu opens the sidebar as a drawer; a red dot on chats means unread. */
export const TabBar = ({ onMenu, unread = 0, isDark }) => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const { pathname } = useLocation();
  const tab = (icon, label, path, selected, dot = false) => (
    <IconButton
      size="m"
      variant={selected ? "gray" : "plain"}
      selected={selected}
      label={label}
      aria-current={selected ? "page" : undefined}
      onClick={() => navigate(path)}
    >
      <Icon name={icon} size={22} />
      {dot && <span className="absolute bottom-[7px] right-[7px] size-[9px] rounded-full bg-status-danger" aria-hidden="true" />}
    </IconButton>
  );
  return (
    <nav
      aria-label={t("navigation.navigation")}
      className="fixed inset-x-0 bottom-0 z-30 flex h-[72px] items-center justify-between border-t border-separator-default bg-material-regular px-space-20 pb-space-12 pt-space-8 font-ds text-label-primary backdrop-blur-[30px]"
    >
      <IconButton size="m" variant="plain" label={t("navigation.menu")} onClick={onMenu}>
        <Icon name="menu" size={22} />
      </IconButton>
      {tab("home", t("navigation.home"), "/home", pathname === "/home")}
      <CreateButton />
      {tab(
        "send",
        unread ? `${t("messages.messages")}, ${unread}` : t("messages.messages"),
        "/messages",
        pathname.startsWith("/messages"),
        unread > 0
      )}
      {tab("compass", t("navigation.discover"), "/", pathname === "/")}
    </nav>
  );
};
